import _ from 'lodash'
import { jwtDecode } from "jwt-decode";
import Config from "./../config/config";
const { cacheForm = true } = Config;

const FORM_CACHE_DB_NAME = 'marsFormCacheDB';
const FORM_CACHE_STORE_NAME = 'formCache';
const FORM_CACHE_DURATION_MS = 60 * 60 * 1000;
let formCacheDbPromise;
let formCacheDbInstance;

const getFormCacheDb = () => {
    if (formCacheDbPromise) {
        return formCacheDbPromise;
    }

    formCacheDbPromise = new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || !window.indexedDB) {
            reject(new Error('IndexedDB is not available'));
            return;
        }

        const request = window.indexedDB.open(FORM_CACHE_DB_NAME, 1);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(FORM_CACHE_STORE_NAME)) {
                db.createObjectStore(FORM_CACHE_STORE_NAME, { keyPath: 'key' });
            }
        };

        request.onsuccess = () => {
            formCacheDbInstance = request.result;
            resolve(request.result);
        };
        request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
    }).catch((error) => {
        formCacheDbPromise = null;
        formCacheDbInstance = null;
        throw error;
    });

    return formCacheDbPromise;
};

const withFormCacheStore = async (mode, callback) => {
    const db = await getFormCacheDb();

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(FORM_CACHE_STORE_NAME, mode);
        const store = transaction.objectStore(FORM_CACHE_STORE_NAME);

        let callbackResult;
        try {
            callbackResult = callback(store, resolve, reject);
        } catch (error) {
            reject(error);
            return;
        }

        transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));

        if (callbackResult !== undefined) {
            resolve(callbackResult);
        }
    });
};

export const setLoginToken = (token, key = 'authToken') => {
    const now = new Date();
    const ttl = 3600000 /* One Hour */ * 24;

    const item = {
        value: token,
        expiry: now.getTime() + ttl, // ttl is in milliseconds
    };
    localStorage.setItem(key, JSON.stringify(item));
};

export const getLoginToken = (key = 'authToken') => {
    const itemStr = localStorage.getItem(key);

    if (!itemStr) {
        return null;
    }

    const item = JSON.parse(itemStr);
    const now = new Date();

    if (now.getTime() > item.expiry) {
        removeLoginToken(key)
        return null;
    }

    return item.value;
};

export const getUserFromToken = () => {
    const token = getLoginToken();
    try {
        const decoded = jwtDecode(token);
        return decoded;
    } catch (error) {
        console.error("Invalid token:", error);
        return {};
    }
}

export const fetchFromCache = async (key) => {
    if (!cacheForm) {
        return null;
    }

    try {
        const cacheKey = `formCache_${key}`;
        const cacheItem = await withFormCacheStore('readonly', (store, resolve, reject) => {
            const request = store.get(cacheKey);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error || new Error('Failed to read cache from IndexedDB'));
        });

        if (!cacheItem) {
            return null;
        }

        if (Date.now() < cacheItem.expiry) {
            return cacheItem.data;
        }

        await withFormCacheStore('readwrite', (store, resolve, reject) => {
            const deleteRequest = store.delete(cacheKey);
            deleteRequest.onsuccess = () => resolve(true);
            deleteRequest.onerror = () => reject(deleteRequest.error || new Error('Failed to delete expired cache item'));
        });

        return null;
    } catch (error) {
        console.error('fetchFromCache failed:', error);
        return null;
    }
};

export const saveFormToCache = async (key, data) => {
    if (!cacheForm)
        return;
    return saveToCache(key, data);
}

export const saveToCache = async (key, data) => {
    try {
        const cacheKey = `formCache_${key}`;
        const cacheItem = {
            key: cacheKey,
            data,
            expiry: Date.now() + FORM_CACHE_DURATION_MS
        };

        await withFormCacheStore('readwrite', (store, resolve, reject) => {
            const request = store.put(cacheItem);
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error || new Error('Failed to save cache to IndexedDB'));
        });
    } catch (error) {
        console.error('saveToCache failed:', error);
    }
};

export const removeLoginToken = (key = 'authToken') => {
    localStorage.removeItem(key);
}

export const clearFormCache = async () => {
    const formCachePrefixes = ['formCache_', 'formCacheExpiry_'];
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
        if (formCachePrefixes.some((prefix) => key.startsWith(prefix))) {
            localStorage.removeItem(key);
        }
    });

    try {
        await withFormCacheStore('readwrite', (store, resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error || new Error('Failed to clear IndexedDB form cache'));
        });
    } catch (error) {
        console.error('clearFormCache failed:', error);
    }

    try {
        if (typeof window === 'undefined' || !window.indexedDB) {
            return;
        }
        if (formCacheDbInstance) {
            formCacheDbInstance.close();
            formCacheDbInstance = null;
        }
        formCacheDbPromise = null;

        await new Promise((resolve, reject) => {
            const request = window.indexedDB.deleteDatabase(FORM_CACHE_DB_NAME);
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error || new Error('Failed to delete IndexedDB form cache'));
            request.onblocked = () => resolve(false);
        });
    } catch (error) {
        console.error('clearFormCache deleteDatabase failed:', error);
    }
}

export const configToStrategy = (config) => {
    // console.log("config", config);
    const { data = {} } = config;
    if (_.isEmpty(data))
        return {};
    const { companyPayload = {}, indicators = [], conditions = [], buyAt = '', sellAt = '', buyDateRange = [], buyAtEveryDip = [], sellConditions = [], profitTargetPercentage = 0, stopLossPercentage = 100, trailingStopLossPercentage = 0, runPreDates = false, allowOverlap = false,
        sellDateSameRange = false, alertMessageTitle = '', userChatId = '', userChatIds = [], userIdIn = [], alertFields = {}, performAnalysis = false, preAnalysis = {}, postAnalysis = {}
    } = data;
    const { analysisType: preAnalysisType = '', analysisValue: preAnalysisValue = 0 } = preAnalysis;
    const { analysisType: postAnalysisType = '', analysisValue: postAnalysisValue = 0 } = postAnalysis;
    const _indicators = indicators.map(indicator => {
        const { type = '', macdLineDays = [], ...restData } = indicator;
        if (type == 'MACD') {
            return { type, ...restData, macdLow: macdLineDays[0], macdHigh: macdLineDays[1], }
        }
        return indicator;
    })
    const _buyAtEveryDip = buyAtEveryDip.map(dip => {
        return { dipPercent: dip[0], buyPercent: dip[1] }
    });

    const _userChatIds = [...userChatIds, userChatId].filter(data => data.length > 0).join(";");

    const alertKeys = Object.keys(alertFields);
    const _alertFields = alertKeys.map(key => ({ key, value: alertFields[key] }));

    const _conditions = { condition: conditions };
    const genericFields = {
        buyAt, sellAt, sellConditions, alertMessageTitle, userChatIds: _userChatIds,
        profitTargetPercentage, stopLossPercentage, trailingStopLossPercentage, runPreDates, allowOverlap, sellDateSameRange,
        buyDateStart: buyDateRange[0] || '', buyDateEnd: buyDateRange[1] || '',
        performAnalysis, preAnalysisType, preAnalysisValue, postAnalysisType, postAnalysisValue, userIdIn
    };


    return {
        companyPayload,
        indicators: _indicators,
        conditions: _conditions,
        genericFields,
        buyAtEveryDip: _buyAtEveryDip,
        alertFields: _alertFields
    };
}

export const cleanObject_old = (obj, excludes = [null]) => {
    if (Array.isArray(obj)) {
        return obj.map(data => cleanObject(data, excludes));
    } else if (typeof obj === 'object' && !excludes.includes(obj)/* obj != null*/) {
        const keys = Object.keys(obj);
        return keys.reduce((accum, key) => {
            if (!excludes.includes(obj[key])) return { ...accum, [key]: cleanObject(obj[key], excludes) }
            return { ...accum };
        }, {})
    }
    return obj;
}

export const cleanObject = (value) => {
    // Remove null, undefined, empty string
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    // Handle arrays
    if (Array.isArray(value)) {
        const cleanedArray = value
            .map(item => cleanObject(item))
            .filter(item => item !== undefined);

        return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    // Handle objects
    if (typeof value === "object") {
        const cleanedObject = Object.entries(value).reduce((acc, [key, val]) => {
            const cleanedVal = cleanObject(val);
            if (cleanedVal !== undefined) {
                acc[key] = cleanedVal;
            }
            return acc;
        }, {});

        return Object.keys(cleanedObject).length > 0
            ? cleanedObject
            : undefined;
    }

    // Keep all other values (numbers, booleans, dates, etc.)
    return value;
};

export const strategyToConfig = (payload) => {
    const { companyPayload = {}, indicators = [], conditions = [], genericFields = {}, buyAtEveryDip = [], alertFields = [] } = payload;

    const { buyAt = '', sellAt = '', sellConditions = [], profitTargetPercentage = 0, buyDateStart = '', buyDateEnd = '', stopLossPercentage = 100, trailingStopLossPercentage = 0, alertMessageTitle, userChatIds = '', runPreDates, allowOverlap, sellDateSameRange, performAnalysis = false, preAnalysisType, preAnalysisValue, userIdIn = [],
        postAnalysisType, postAnalysisValue,
    } = genericFields;

    const _indicators = indicators.map(indicator => {
        const { type = '', macdLow = 0, macdHigh = 0, ...restData } = indicator;
        if (type == 'MACD') {
            return { type, ...restData, macdLineDays: [macdLow, macdHigh] }
        }
        return indicator;
    })

    const _buyAtEveryDip = buyAtEveryDip.map(dip => {
        const { dipPercent = 0, buyPercent = 0 } = dip;
        return [dipPercent, buyPercent]
    });

    const _alertFields = alertFields.reduce((accum, data) => {
        return { ...accum, [data.key]: data.value };
    }, {});

    const _userChatIds = userChatIds.split(";").filter(data => data.length > 0);

    const preAnalysis = cleanObject({ analysisType: preAnalysisType, analysisValue: preAnalysisValue });
    const postAnalysis = cleanObject({ analysisType: postAnalysisType, analysisValue: postAnalysisValue });

    return {
        companyPayload,
        indicators: cleanObject(_indicators),
        conditions: conditions.condition, runPreDates, allowOverlap, sellDateSameRange,
        buyAt, sellAt, sellConditions, profitTargetPercentage, stopLossPercentage, trailingStopLossPercentage,
        buyDateRange: buyDateStart && buyDateEnd ? [buyDateStart, buyDateEnd] : [],
        buyAtEveryDip: _buyAtEveryDip, alertMessageTitle, userChatIds: _userChatIds,
        alertFields: _alertFields, preAnalysis, userIdIn,
        postAnalysis, performAnalysis,
    }
}

export const formatValueWithType = (value, type = 'float', precision = 2) => {
    switch (type) {
        case "float": return value === "" ? null : parseFloat(parseFloat(value).toFixed(precision))
        case "int": return value === "" ? null : parseInt(value)
        default: return value
    }
}

export const formatIndianCurrency = (value, precision = 2) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
        return value;
    }
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
    });
}

export const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export const toProperString = (str) => {
    return str
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}


// Source - https://stackoverflow.com/a/1349426
// Posted by csharptest.net, modified by community. See post 'Timeline' for change history
// Retrieved 2026-02-01, License - CC BY-SA 4.0

export const randomString = (length) => {
    var result = '';
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}
